import type * as common from '../common'
import type * as dms from '../dms'

import type { Agent } from './agent'
import type { Conversation } from './conversation'
import * as agentFile from './file'
import type { KnowledgeBase } from './knowledge-base'

/**
 * Represents the context configuration passed to a Bedrock agent for a specific conversation.
 * Stores which DMS entities and files (S3 or knowledge-base references) the agent should use.
 *
 * The natural key is `(agentId, conversationId)` — the agent default has no
 * `conversationId`, while per-conversation overrides do.
 */
export interface Configuration {
  agentId: Agent.Id
  conversationId?: Conversation.Id
  context: Configuration.Context
}
export namespace Configuration {
  /**
   * The JSONB payload stored in Postgres describing what context to pass to the agent.
   */
  export interface Context {
    /**
     * A saved DMS query whose selection returns the records and S3 file URIs
     * to include as agent context.
     */
    dmsResources?: dms.data.query.ForSingleton<{
      records: readonly dms.data.Record.Values[]
      files: readonly string[]
    }>
    /** Knowledge-base files to include as agent context. */
    knowledgeBaseFiles?: readonly KnowledgeBase.File[]
    /**
     * Free-form natural-language instructions guiding how the agent
     * should use the provided resources (e.g. "prefer recent records",
     * "ignore archived files").
     */
    instructions?: string
  }
  export namespace Context {
    /**
     * Helpers for files that belong to a {@link Configuration.Context} —
     * i.e. files attached to the *conversation* (or to the agent default),
     * not to any specific {@link Message}. Their S3 keys must be reusable
     * across every turn, so the qualifier never includes a message id.
     */
    export namespace File {
      /**
       * Generates a {@link common.fileManagement.Key.Parts.qualifier qualifier}
       * for a context file. Per-conversation configurations are scoped to the
       * `conversationId`; the agent-default configuration (no conversation)
       * falls back to the `agentId`.
       */
      export function generateQualifier(
        params: generateQualifier.Params,
      ): string {
        const { agentId, conversationId } = params
        const scope = conversationId ?? agentId
        return `context_${scope}`.replace(/[^a-zA-Z0-9-_]/g, '')
      }
      export namespace generateQualifier {
        export interface Params {
          /** {@link Agent.Id} that owns the context (used as fallback scope). */
          agentId: Agent.Id
          /**
           * {@link Conversation.id} the context belongs to. When omitted (i.e.
           * the agent-default configuration), the qualifier is scoped to
           * `agentId` instead.
           */
          conversationId?: Conversation['id']
        }
      }

      /**
       * Builds Bedrock-compatible S3 file references for context files.
       *
       * @example
       * const refs = Configuration.Context.File.toS3References({
       *   context,
       *   files: ['policies.pdf'],
       *   qualifier: { agentId, conversationId },
       *   bucketName: 'my-bucket',
       * })
       */
      export function toS3References(
        params: toS3References.Params,
      ): agentFile.S3Reference[] {
        const { context, files, qualifier, bucketName } = params
        const resolvedQualifier = generateQualifier(qualifier)
        return files.map(fileName =>
          agentFile.buildS3Reference({
            bucketName,
            context,
            fileName,
            qualifier: resolvedQualifier,
          }),
        )
      }
      export namespace toS3References {
        export interface Params {
          /** Context for key generation. */
          context: common.fileManagement.Key.Parts.Context
          /** File names to build references for. */
          files: readonly string[]
          /** Qualifier params for the S3 key. */
          qualifier: generateQualifier.Params
          /** S3 bucket name. */
          bucketName: string
        }
      }
    }
  }

  export type Input = Configuration
}
